import React, { useCallback } from "react";
import { cloneDeep, debounce, get } from "lodash";
import PropTypes from "prop-types";
import { Select, Box, Toggle } from "orcs-design-system";
import {
  useFilters,
  useApplyFilters,
} from "src/contexts/filterAndFte/FilterAndFteContext";
import { DEBOUNCE_SEARCH_TIME } from "src/consts/global";
import FilterOptionPropType from "../../../custom-prop-types/filterOption";
import formatGroupLabel from "./formatGroupLabel";
import formatOptionLabel from "./formatOptionLabel";
import { updateFilterValues } from "./filterConfigs";

const FilterSelect = (props) => {
  const {
    onInputChange,
    isSearching,
    options,
    valueKeys,
    toggles,
    name,
    optionLabelType,
    filterOption,
    backspaceRemovesValue,
    ...selectProps
  } = props;

  const filters = useFilters();

  const applyFilters = useApplyFilters();
  const filterValue = valueKeys.map((key) => get(filters, key, [])).flat();

  const onFilterValueChange = useCallback(
    (values) => {
      const updatedFilters = updateFilterValues(
        filters,
        name,
        valueKeys,
        values
      );
      applyFilters(updatedFilters);
    },
    [filters, name, applyFilters, valueKeys]
  );

  const onSelectGroupLabel = useCallback(
    (key, value) => {
      const currentFilters = cloneDeep(filters);
      currentFilters[key].unshift(value);
      applyFilters(currentFilters);
    },
    [filters, applyFilters]
  );

  const select = (
    <Select
      placeholder="Search or select"
      {...selectProps}
      options={options}
      value={filterValue}
      filterOption={filterOption}
      onChange={(value) => onFilterValueChange(value)}
      onInputChange={
        onInputChange && debounce(onInputChange, DEBOUNCE_SEARCH_TIME)
      }
      backspaceRemovesValue={backspaceRemovesValue}
      isLoading={isSearching}
      formatOptionLabel={formatOptionLabel[optionLabelType]}
      formatGroupLabel={(data) => formatGroupLabel(data, onSelectGroupLabel)}
    />
  );

  return (
    <Box mb="r">
      {select}
      {toggles.length > 0 && (
        <Box mt="r">
          {toggles.map((toggle) => (
            <Toggle
              key={toggle.id}
              small
              id={toggle.id}
              label={toggle.label}
              className={`guided-tour-filter-toggle-${toggle.id}`}
              checked={get(filters, toggle.valueKey)}
              onChange={(e) => {
                onFilterValueChange({
                  value: e.target.checked,
                  valueKey: toggle.valueKey,
                });
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

FilterSelect.defaultProps = {
  isSearching: false,
  filterOption: false,
  backspaceRemovesValue: false,
  toggles: [],
};

FilterSelect.propTypes = {
  options: PropTypes.arrayOf(FilterOptionPropType).isRequired,
  onInputChange: PropTypes.func.isRequired,
  isSearching: PropTypes.bool,
  valueKeys: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  optionLabelType: PropTypes.string,
  toggles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      valueKey: PropTypes.string.isRequired,
    })
  ),
  name: PropTypes.string.isRequired,
  filterOption: PropTypes.bool,
  backspaceRemovesValue: PropTypes.bool,
};

export default FilterSelect;
